import { isMarketingAllowed, type CookieConsent } from "./cookieConsentService";
import type { RegionBucket } from "./regionService";

// Standard Meta Pixel bootstrap, injected dynamically at runtime — never
// hardcoded in index.html — so the script only loads once marketing consent
// is established. Loading it unconditionally would break GDPR compliance in
// the EU/UK, where consent defaults to denied (see cookieConsentService).

type MetaPixelFbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: MetaPixelFbq;
  loaded?: boolean;
  version?: string;
};

type MetaPixelWindow = Window & {
  fbq?: MetaPixelFbq;
  _fbq?: MetaPixelFbq;
};

const PIXEL_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const FBP_COOKIE_NAME = "_fbp";
const FBC_COOKIE_NAME = "_fbc";

let didWarnNonProdRuntime = false;
let didWarnMissingPixelId = false;
let didInjectScript = false;
let didInitPixel = false;
let resolvedPixelId: string | null | undefined;

function getMetaPixelWindow(): MetaPixelWindow | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window as MetaPixelWindow;
}

/**
 * The dev server runs as `vite --mode production` (see package.json), so local
 * development loads .env.production — including the live pixel id. Vite still
 * reports PROD=false when serving rather than building, which is the only
 * signal that separates a developer's machine from a real deploy. Without this
 * guard every local session would report conversions into the production
 * pixel and corrupt campaign optimisation data. Mirrors the same
 * `import.meta.env.PROD` gate analyticsService uses for the Google Ads tag.
 */
function isProdRuntime(): boolean {
  if (import.meta.env.PROD) {
    return true;
  }
  if (!didWarnNonProdRuntime) {
    didWarnNonProdRuntime = true;
    console.info("[metaPixel] Non-production runtime; Meta Pixel disabled");
  }
  return false;
}

function getPixelId(): string | null {
  if (resolvedPixelId !== undefined) {
    return resolvedPixelId;
  }
  const raw = import.meta.env.VITE_META_PIXEL_ID?.trim();
  // Held in a local as well as the module-level cache: the console.info() call
  // below would otherwise reset TypeScript's narrowing of the mutable
  // module-scoped binding back to `string | null | undefined`.
  const pixelId = raw ? raw : null;
  resolvedPixelId = pixelId;
  if (!pixelId && !didWarnMissingPixelId) {
    didWarnMissingPixelId = true;
    console.info("[metaPixel] VITE_META_PIXEL_ID not set; Meta Pixel disabled");
  }
  return pixelId;
}

/**
 * Installs the minimal fbq stub (the standard Meta Pixel snippet) so calls
 * queue up before fbevents.js finishes loading, then injects the script tag.
 * Idempotent: guards against React StrictMode double-mount and against an
 * fbq already present on window (e.g. injected by something else).
 */
function ensureFbqStub(pixelWindow: MetaPixelWindow): MetaPixelFbq {
  if (typeof pixelWindow.fbq === "function") {
    return pixelWindow.fbq;
  }

  const fbq: MetaPixelFbq = function fbqStub(...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue?.push(args);
    }
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  pixelWindow.fbq = fbq;
  pixelWindow._fbq = pixelWindow._fbq ?? fbq;

  return fbq;
}

function injectPixelScript(): void {
  if (didInjectScript) {
    return;
  }
  if (typeof document === "undefined") {
    return;
  }

  const existing = document.querySelector(`script[src="${PIXEL_SCRIPT_SRC}"]`);
  if (existing) {
    didInjectScript = true;
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = PIXEL_SCRIPT_SRC;
  document.head?.appendChild(script);
  didInjectScript = true;
}

function callFbq(...args: unknown[]): void {
  const pixelWindow = getMetaPixelWindow();
  if (!pixelWindow || typeof pixelWindow.fbq !== "function") {
    return;
  }
  pixelWindow.fbq(...args);
}

export function initializeMetaPixel(
  regionBucket: RegionBucket | undefined,
  consent: CookieConsent | null
): void {
  try {
    if (!isProdRuntime()) {
      return;
    }
    const pixelId = getPixelId();
    if (!pixelId) {
      return;
    }
    if (!isMarketingAllowed(regionBucket, consent)) {
      return;
    }

    const pixelWindow = getMetaPixelWindow();
    if (!pixelWindow) {
      return;
    }

    ensureFbqStub(pixelWindow);
    injectPixelScript();

    if (didInitPixel) {
      return;
    }
    didInitPixel = true;

    callFbq("init", pixelId);
    callFbq("track", "PageView");
  } catch (error) {
    console.warn("[metaPixel] Failed to initialize Meta Pixel:", error);
  }
}

/**
 * The pixel script cannot be unloaded once injected, so when a user
 * withdraws marketing consent after it already loaded, the correct
 * mechanism is telling it to revoke consent going forward.
 */
export function revokeMetaPixelConsent(): void {
  try {
    if (!getPixelId() || !didInitPixel) {
      return;
    }
    callFbq("consent", "revoke");
  } catch (error) {
    console.warn("[metaPixel] Failed to revoke Meta Pixel consent:", error);
  }
}

export function isMetaPixelReady(): boolean {
  try {
    return didInitPixel && !!getPixelId();
  } catch (error) {
    console.warn("[metaPixel] Failed to read Meta Pixel readiness:", error);
    return false;
  }
}

export function trackPixelEvent(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string
): void {
  try {
    if (!getPixelId() || !didInitPixel) {
      return;
    }
    if (eventId) {
      callFbq("track", eventName, params, { eventID: eventId });
    } else {
      callFbq("track", eventName, params);
    }
  } catch (error) {
    console.warn("[metaPixel] Failed to track Meta Pixel event:", error);
  }
}

export function trackPixelCustomEvent(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string
): void {
  try {
    if (!getPixelId() || !didInitPixel) {
      return;
    }
    if (eventId) {
      callFbq("trackCustom", eventName, params, { eventID: eventId });
    } else {
      callFbq("trackCustom", eventName, params);
    }
  } catch (error) {
    console.warn("[metaPixel] Failed to track Meta Pixel custom event:", error);
  }
}

export function trackPixelPageView(): void {
  trackPixelEvent("PageView");
}

function readCookieValue(cookieName: string): string | undefined {
  try {
    if (typeof document === "undefined" || typeof document.cookie !== "string") {
      return undefined;
    }
    const cookies = document.cookie.split(";");
    for (const rawCookie of cookies) {
      const cookie = rawCookie.trim();
      const eqIndex = cookie.indexOf("=");
      if (eqIndex === -1) continue;
      const name = cookie.slice(0, eqIndex);
      if (name === cookieName) {
        return cookie.slice(eqIndex + 1);
      }
    }
    return undefined;
  } catch (error) {
    console.warn(`[metaPixel] Failed to read ${cookieName} cookie:`, error);
    return undefined;
  }
}

export function getFbp(): string | undefined {
  return readCookieValue(FBP_COOKIE_NAME);
}

export function getFbc(): string | undefined {
  return readCookieValue(FBC_COOKIE_NAME);
}

export function newEventId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (error) {
    console.warn("[metaPixel] crypto.randomUUID unavailable, using fallback:", error);
  }
  const randomPart = Math.random().toString(36).slice(2, 12);
  return `${Date.now().toString(36)}_${randomPart}`;
}
